var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { hashToBuffer, SchemaValidationError } from "../shared/utils";
import { MerkleTree } from "../shared/merkle";
import { SchemaId } from "../shared/@types/document";
import { digestCredential } from "../3.0/digest";
import { validateSchema as validate } from "../shared/validate";
import { encodeSalt, salt } from "./salt";
import { validateW3C } from "./validate";
import { getSchema } from "../shared/ajv";
const getExternalSchema = (schema) => (schema ? { schema } : {});
/**
 * @deprecated will be removed in the next major release in favour of OpenAttestation v4.0 (more info: https://github.com/Open-Attestation/open-attestation/tree/alpha)
 */
export const wrapDocument = (credential, options) => __awaiter(void 0, void 0, void 0, function* () {
    const document = Object.assign(Object.assign({ version: SchemaId.v3 }, getExternalSchema(options.externalSchemaId)), credential);
    // To ensure that base @context exists, but this also means some of our validateW3C errors may be unreachable
    if (!document["@context"]) {
        document["@context"] = ["https://www.w3.org/2018/credentials/v1"];
    }
    // Since our wrapper adds in OA-specific properties, we should push our OA context. This is also to pass W3C VC test suite.
    if (Array.isArray(document["@context"]) &&
        !document["@context"].includes("https://schemata.openattestation.com/com/openattestation/1.0/OpenAttestation.v3.json")) {
        document["@context"].push("https://schemata.openattestation.com/com/openattestation/1.0/OpenAttestation.v3.json");
    }
    const salts = salt(document);
    const digest = digestCredential(document, salts, []);
    const batchBuffers = [digest].map(hashToBuffer);
    const merkleTree = new MerkleTree(batchBuffers);
    const merkleRoot = merkleTree.getRoot().toString("hex");
    const merkleProof = merkleTree.getProof(hashToBuffer(digest)).map((buffer) => buffer.toString("hex"));
    const verifiableCredential = Object.assign(Object.assign({}, document), { proof: {
            type: "OpenAttestationMerkleProofSignature2018",
            proofPurpose: "assertionMethod",
            targetHash: digest,
            proofs: merkleProof,
            merkleRoot,
            salts: encodeSalt(salts),
            privacy: {
                obfuscated: [],
            },
        } });
    const errors = validate(verifiableCredential, getSchema(SchemaId.v3));
    if (errors.length > 0) {
        throw new SchemaValidationError("Invalid document", errors, verifiableCredential);
    }
    yield validateW3C(verifiableCredential);
    return verifiableCredential;
});
/**
 * @deprecated will be removed in the next major release in favour of OpenAttestation v4.0 (more info: https://github.com/Open-Attestation/open-attestation/tree/alpha)
 */
export const wrapDocuments = (documents, options) => __awaiter(void 0, void 0, void 0, function* () {
    // create individual verifiable credential
    const verifiableCredentials = yield Promise.all(documents.map((document) => wrapDocument(document, options)));
    // get all the target hashes to compute the merkle tree and the merkle root
    const merkleTree = new MerkleTree(verifiableCredentials.map((verifiableCredential) => verifiableCredential.proof.targetHash).map(hashToBuffer));
    const merkleRoot = merkleTree.getRoot().toString("hex");
    // for each document, update the merkle root and add the proofs needed
    return verifiableCredentials.map((verifiableCredential) => {
        const digest = verifiableCredential.proof.targetHash;
        const merkleProof = merkleTree.getProof(hashToBuffer(digest)).map((buffer) => buffer.toString("hex"));
        return Object.assign(Object.assign({}, verifiableCredential), { proof: Object.assign(Object.assign({}, verifiableCredential.proof), { proofs: merkleProof, merkleRoot }) });
    });
});
